import {
  ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
  ROUTE_PUBLIC_NARRATIVE_CREATE,
  ROUTE_PUBLIC_NARRATIVE_EDIT,
} from '@/constants/routes';
import { useLocation } from '@/hooks/useLocation';
import { useQueryStringParam } from '@/hooks/useQueryStringParam';
import { getPath } from '@/router/utils';
import type { FragmentOf } from '@/utilities/gql';
import { gql } from '@/utilities/gql';
import { isAfter } from 'date-fns';

import { useEffect } from 'react';
import { replace } from 'redux-first-router';
import { NARRATIVE_VALUATOR_QUERY_FIELD_MAP } from '../utils/transformation';

export const USE_EXISTING_NARRATIVE_REDIRECT_COMPANY_FRAGMENT = gql(`
  fragment UseExistingNarrativeRedirectCompanyFields on Company @_unmask {
    id
    myNarrative {
      id
      owner {
        publicId
      }
      latestPublishedUpdate {
        id
        willLockAt
      }
      earliestPublishedUpdate {
        id
      }
    }
  }
`);

type Props = {
  company:
    | FragmentOf<typeof USE_EXISTING_NARRATIVE_REDIRECT_COMPANY_FRAGMENT>
    | undefined
    | null;
  isEnabled: boolean;
};

/**
 * Takes a user to the existing narrative edit page if the user already created a narrative for this company
 */
export const useExistingNarrativeRedirect = ({ company, isEnabled }: Props) => {
  const { getItem } = useQueryStringParam();
  const { type } = useLocation();
  const { myNarrative, id: companyId } = company ?? {};
  const {
    id: userNarrativeId,
    latestPublishedUpdate,
    earliestPublishedUpdate,
  } = myNarrative ?? {};

  const hasNarrative = !!userNarrativeId;
  const isEditableWithinTimelock =
    latestPublishedUpdate?.willLockAt &&
    isAfter(new Date(latestPublishedUpdate.willLockAt), new Date());

  const isNarrativeDraft = !latestPublishedUpdate;
  const isNarrative =
    isNarrativeDraft ||
    (latestPublishedUpdate &&
      earliestPublishedUpdate?.id === latestPublishedUpdate?.id &&
      isEditableWithinTimelock);

  const sharedQueryParams = [
    'returnPath',
    ...Object.values(NARRATIVE_VALUATOR_QUERY_FIELD_MAP).map(
      ({ name }) => name
    ),
  ].reduce((acc, queryParamName) => {
    const value = getItem(queryParamName);
    if (value) acc[queryParamName] = value;
    return acc;
  }, {});

  const getRedirectPath = () => {
    if (!companyId || !isEnabled) return;

    // New flow post-updates - supports redirect to create, edit, or edit/create update
    // User has no narrative yet and they are not on the create page -> redirect to create
    if (!hasNarrative) {
      if (type === ROUTE_PUBLIC_NARRATIVE_CREATE) return;

      return getPath({
        type: ROUTE_PUBLIC_NARRATIVE_CREATE,
        payload: {
          query: {
            ...sharedQueryParams,
            company: companyId,
          },
        },
      });
    }

    if (!userNarrativeId) return;

    // User has a narrative that either draft or published but editable within timelock and they are not on the edit page -> redirect to edit
    if (isNarrative) {
      if (type === ROUTE_PUBLIC_NARRATIVE_EDIT) return;
      return getPath({
        type: ROUTE_PUBLIC_NARRATIVE_EDIT,
        payload: {
          id: userNarrativeId,
          query: {
            ...sharedQueryParams,
            company: companyId,
          },
        },
      });
    }

    // User has either has a locked narrative or a draft update
    // or editable update within grace period and they are not on the edit/create update page -> redirect to edit/create update
    if (type !== ROUTE_NARRATIVE_UPDATE_CREATE_EDIT)
      return getPath({
        type: ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
        payload: {
          companyid: companyId,
          query: {
            ...sharedQueryParams,
          },
        },
      });
  };

  const redirectPath = getRedirectPath();

  useEffect(() => {
    if (!redirectPath) return;

    replace(redirectPath);
  }, [redirectPath]);
};
